import { pipe } from 'fp-ts/function';
import { array, boolean, option, ord, string } from 'fp-ts';
import { TextKey } from '@mews-commander/enums';
import { LocalizedSmallLabel, Loader } from '@mews-commander/ui';
import { Card, CardContent, Divider, Typography, Stack } from '@optimus-web/core';
import { intersperse } from '@mews/fp';
import { ReservationTimeState } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import {
    getCurrentEnterpriseReservationOccupancyData,
    getCurrentEnterpriseReservationOwnerId,
    getCurrentEnterpriseReservationBookerId,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getCompanionshipsAccountIdsByReservationIds } from '@mews-commander/legacy-entities/src/ducks/companionships';
import { useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { areBalancesFetching } from '../../../ducks/balances';
import { ReservationPersonCount } from '../../../../../components/reservation/ReservationPersonCount';
import { getCustomerVerificationPhoto, useGuestsPhotosQuery } from '../../../../../accounts/modules/verificationPhoto';
import { CustomerBalanceLabel } from './CustomerBalanceLabel';
import { TotalBalanceLabel } from './TotalBalanceLabel';
import { balanceLoadTimeState } from './constants';
import { SmartTipsCustomerList } from './SmartTipsCustomerList';

interface BalancesProps {
    reservationId: string;
    serviceId: string;
    groupId: string;
    reservationTimeState: ReservationTimeState;
}

export const Balances = ({ reservationId, serviceId, groupId, reservationTimeState }: BalancesProps) => {
    const showBalances = balanceLoadTimeState.includes(reservationTimeState);
    const enterpriseId = useCurrentEnterpriseId();
    const { chainId } = useEnterprise(enterpriseId);

    const accountId = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationOwnerId(state, { id: reservationId })
    );
    const bookerId = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationBookerId(state, { id: reservationId })
    );
    const companionAccountIds = useCommanderSelector((state) =>
        getCompanionshipsAccountIdsByReservationIds(state, {
            reservationIds: [reservationId],
        })
    );
    const occupancyData = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationOccupancyData(state, { id: reservationId })
    );
    const isLoading = useCommanderSelector(areBalancesFetching);

    const nonCompanionActorIds = pipe(
        [accountId, bookerId],
        array.map(option.fromNullable),
        array.compact,
        array.filter((customerId) => !companionAccountIds.includes(customerId)),
        array.uniq(string.Eq)
    );

    const { data: guestsVerificationPhotoData } = useGuestsPhotosQuery({
        guestIds: companionAccountIds,
    });

    const byOwnerIdMatch = ord.contramap((customerId: string) => customerId === accountId)(boolean.Ord);
    const byBookerIdMatch = ord.contramap((customerId: string) => bookerId !== null && customerId === bookerId)(
        boolean.Ord
    );

    const renderCustomerLabels = (customerIds: string[]) =>
        array.isNonEmpty(customerIds) && (
            <Stack vertical spacing="100">
                {pipe(
                    customerIds,
                    array.sortBy([ord.reverse(byOwnerIdMatch), ord.reverse(byBookerIdMatch)]),
                    array.map((customerId) => (
                        <Stack vertical spacing="100">
                            <CustomerBalanceLabel
                                customerId={customerId}
                                reservationId={reservationId}
                                showBalance={showBalances}
                                guestVerificationPhotoSrc={
                                    getCustomerVerificationPhoto(customerId, guestsVerificationPhotoData)?.imageUrl ??
                                    null
                                }
                                key={`customer-balance-label-${customerId}`}
                            />
                            <SmartTipsCustomerList
                                id={customerId}
                                chainId={chainId}
                                enterpriseIds={enterpriseId}
                                key={`smart-tips-customer-list-${customerId}`}
                            />
                        </Stack>
                    )),
                    intersperse((index: number) => <Divider key={index} />)
                )}
            </Stack>
        );

    const companionsContent = isLoading ? (
        <Loader />
    ) : (
        <Stack vertical spacing="200">
            <Stack spacing="100" distribution="space-between" data-test-guests-person-count>
                <LocalizedSmallLabel textKey={TextKey.GuestPlural} as="div" />
                <Typography textStyle="bodySmallStrong" as="div">
                    <ReservationPersonCount serviceId={serviceId} reservationOccupancyData={occupancyData ?? []} />
                </Typography>
            </Stack>

            {renderCustomerLabels(companionAccountIds)}
        </Stack>
    );

    const reservationActorsContent =
        !isLoading && array.isNonEmpty(nonCompanionActorIds) ? renderCustomerLabels(nonCompanionActorIds) : null;

    const totalBalanceContent =
        !isLoading && showBalances ? <TotalBalanceLabel reservationId={reservationId} groupId={groupId} /> : null;

    return (
        <Card overflowHidden padding="0" data-test-smart-detail-card="balances">
            <CardContent padding="200">{companionsContent}</CardContent>
            {reservationActorsContent ? (
                <>
                    <Divider />
                    <CardContent padding="200">
                        <Stack spacing="200" vertical data-test-smart-detail-card-subpart="contacts">
                            <LocalizedSmallLabel textKey={TextKey.Contacts} as="div" />
                            {reservationActorsContent}
                        </Stack>
                    </CardContent>
                </>
            ) : null}
            {totalBalanceContent ? (
                <CardContent padding="200" highlighted data-test-smart-detail-card-subpart="total-balance">
                    {totalBalanceContent}
                </CardContent>
            ) : null}
        </Card>
    );
};
